import { useId, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  addressContext,
  conversationFailedText,
} from 'features/conversation-preferences/address-form';
import { CONVERSATION_PREFERENCES_QUERY_KEY } from 'features/conversation-preferences/hooks/use-conversation-preferences';
import type {
  AddressForm,
  ConversationPreferences,
  ConversationPreferencesPatch,
  Salutation,
} from 'features/conversation-preferences/services/conversation-preferences-service';
import {
  ONBOARDING_INDUSTRY_OPTIONS,
  ONBOARDING_INDUSTRY_OTHER,
} from 'features/onboarding/onboarding-industry';
import { withOnboardingDone } from 'features/onboarding/onboarding-journey';
import { saveSettingsPreferences } from 'features/onboarding/onboarding-preferences';
import { ONBOARDING_TEAM_OPTIONS } from 'features/onboarding/onboarding-script';
import { useToast } from 'hooks/use-toast';
import { useGlobalMutation } from 'state/query-client/hooks';
import { useCommonStore } from 'state/store/common/useCommonStore';
import {
  PERSONAL_MAX_LENGTH,
  personalPatch,
  rebasePersonalValues,
  validatePersonalValues,
  type PersonalFieldError,
  type PersonalTextField,
  type PersonalValues,
} from './personal-values';

type PersonalCardFormProps = {
  /** The saved preference (with the account-name fallback); may change while the form is open. */
  initialValues: PersonalValues;
  /** The stored preference the values come from. */
  stored: ConversationPreferences;
  /** The form of address the app uses now, for the hints and the failure message. */
  addressForm: AddressForm;
  /** False in the English UI, which has a single form. */
  showAddressForm: boolean;
};

/** Salutation choices: preference value, UILM key, English fallback. */
const SALUTATION_OPTIONS: [Salutation, string, string][] = [
  ['frau', 'PROFILE_CONVERSATION_SALUTATION_FRAU', 'Ms'],
  ['herr', 'PROFILE_CONVERSATION_SALUTATION_HERR', 'Mr'],
  ['none', 'PROFILE_CONVERSATION_SALUTATION_NONE', 'No salutation'],
  ['custom', 'PROFILE_CONVERSATION_SALUTATION_CUSTOM', 'Custom …'],
];

/** The area choice that shows the free-text field. */
const AREA_OTHER = 'other';

/** The fields of the "Personal" card. Saves only what changed, in the shared preference queue. */
export function PersonalCardForm({
  initialValues,
  stored,
  addressForm,
  showAddressForm,
}: Readonly<PersonalCardFormProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const id = useId();
  const [saved, setSaved] = useState(initialValues);
  const [values, setValues] = useState(initialValues);
  const [isCustomArea, setIsCustomArea] = useState(false);
  // New server values (a refetch, another tab, the Klax app, the account names arriving) update
  // only the fields the user has not touched, so unsaved edits are never dropped.
  const serverKey = JSON.stringify(initialValues);
  const [seenServerKey, setSeenServerKey] = useState(serverKey);
  if (seenServerKey !== serverKey) {
    setSeenServerKey(serverKey);
    setSaved(initialValues);
    setValues(rebasePersonalValues(saved, initialValues, values));
  }
  const context = addressContext(addressForm);

  const errors = validatePersonalValues(values);
  const hasErrors = Object.keys(errors).length > 0;
  const patch = personalPatch(saved, values, { withAddressForm: showAddressForm });
  const isDirty = Object.keys(patch).length > 0;

  // Set synchronously, so a second click before the re-render can't send a second PUT (C11).
  const inFlightRef = useRef(false);
  const userId = useCommonStore((state) => state.user?.id);
  const save = useGlobalMutation<unknown, unknown, ConversationPreferencesPatch>({
    // A finished onboarding whose last write was lost is marked done with this save (N1).
    mutationFn: (changes) => saveSettingsPreferences(withOnboardingDone(userId, changes, stored)),
  });

  const set = <K extends keyof PersonalValues>(field: K, value: PersonalValues[K]) =>
    setValues((current) => ({ ...current, [field]: value }));

  const handleSave = async () => {
    // C7 and C11: nothing invalid is sent, and a second click while saving is ignored.
    if (inFlightRef.current || !isDirty || hasErrors) return;
    const sent = values;
    inFlightRef.current = true;
    try {
      await save.mutateAsync(patch);
      setSaved(sent);
      // H12: every screen reading the preference (onboarding, catch-up, du/Sie copy) refreshes.
      void queryClient.invalidateQueries({ queryKey: CONVERSATION_PREFERENCES_QUERY_KEY });
      toast({
        title: patch.addressForm
          ? t('PROFILE_CONVERSATION_SAVED', {
              defaultValue: 'Saved.',
              context: addressContext(patch.addressForm),
            })
          : t('PROFILE_CONVERSATION_SAVED_FIELDS', { defaultValue: 'Saved.' }),
      });
    } catch {
      // C6: the typed values stay, and the app keeps its current form.
      toast({
        variant: 'destructive',
        title: conversationFailedText(t, addressForm),
      });
    } finally {
      inFlightRef.current = false;
    }
  };

  const errorText = (field: PersonalTextField, error?: PersonalFieldError) => {
    if (error === 'required') {
      return t('PROFILE_CONVERSATION_REQUIRED', { defaultValue: 'Please fill this in.' });
    }
    if (error === 'tooLong') {
      return t('PROFILE_CONVERSATION_TOO_LONG', {
        defaultValue: 'At most {{max}} characters.',
        max: PERSONAL_MAX_LENGTH[field],
      });
    }
    if (error === 'invalid') {
      return t('PROFILE_CONVERSATION_INVALID', {
        defaultValue: 'Please leave out special characters such as < > or `.',
      });
    }
    return null;
  };

  const textField = (
    field: PersonalTextField,
    label: ReactNode,
    options: { hintId?: string } = {}
  ) => {
    const fieldId = `${id}-${field}`;
    const message = errorText(field, errors[field]);
    const describedBy = [message ? `${fieldId}-error` : null, options.hintId ?? null]
      .filter(Boolean)
      .join(' ');
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={fieldId}>{label}</Label>
        <Input
          id={fieldId}
          value={values[field]}
          onChange={(event) => set(field, event.target.value)}
          aria-invalid={Boolean(message)}
          aria-describedby={describedBy || undefined}
        />
        {message ? (
          <p id={`${fieldId}-error`} className="text-sm text-destructive">
            {message}
          </p>
        ) : null}
      </div>
    );
  };

  const selectField = (
    field: 'salutation' | 'industry' | 'workArea',
    label: string,
    options: { value: string; label: string }[],
    selected: string = values[field],
    onSelect = (value: string) => setValues((current) => ({ ...current, [field]: value }))
  ) => (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${id}-${field}-choice`}>{label}</Label>
      <Select value={selected} onValueChange={onSelect}>
        <SelectTrigger id={`${id}-${field}-choice`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
  const salutationOptions = SALUTATION_OPTIONS.map(([value, key, defaultValue]) => ({
    value,
    label: t(key, { defaultValue }),
  }));
  const industryOptions = [
    ...ONBOARDING_INDUSTRY_OPTIONS,
    { code: 'other', ...ONBOARDING_INDUSTRY_OTHER },
  ].map(({ code, key, defaultValue }) => ({ value: code, label: t(key, { defaultValue }) }));

  // The area is stored as free text: a listed team label, or anything typed under "Something else".
  const areaOptions = [
    ...ONBOARDING_TEAM_OPTIONS.map(({ key, defaultValue }) => {
      const label = t(key, { defaultValue });
      return { value: label, label };
    }),
    { value: AREA_OTHER, label: t('SOMETHING_ELSE', { defaultValue: 'Something else' }) },
  ];
  const isListedArea = areaOptions.some((option) => option.value === values.workArea.trim());
  const showAreaText = isCustomArea || (values.workArea.trim() !== '' && !isListedArea);
  let areaChoice = '';
  if (showAreaText) areaChoice = AREA_OTHER;
  else if (isListedArea) areaChoice = values.workArea.trim();

  const handleAreaSelect = (value: string) => {
    const isOther = value === AREA_OTHER;
    setIsCustomArea(isOther);
    if (isOther && isListedArea) set('workArea', '');
    if (!isOther) set('workArea', value);
  };

  const namesHintId = `${id}-names-hint`;

  return (
    <div className="flex flex-col gap-5">
      {showAddressForm ? (
        <div className="flex flex-col gap-2">
          <Label id={`${id}-address-form`}>
            {t('PROFILE_CONVERSATION_ADDRESS_FORM', { defaultValue: 'Form of address' })}
          </Label>
          <RadioGroup
            aria-labelledby={`${id}-address-form`}
            value={values.addressForm}
            onValueChange={(value) => set('addressForm', value as AddressForm)}
            className="flex flex-wrap gap-6"
          >
            {(['informal', 'formal'] as const).map((form) => (
              <div key={form} className="flex items-center gap-2">
                <RadioGroupItem value={form} id={`${id}-address-${form}`} />
                <Label htmlFor={`${id}-address-${form}`} className="font-normal">
                  {form === 'informal'
                    ? t('PROFILE_CONVERSATION_ADDRESS_INFORMAL', { defaultValue: 'Informal' })
                    : t('PROFILE_CONVERSATION_ADDRESS_FORMAL', { defaultValue: 'Formal' })}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      ) : null}

      <div className="grid gap-5 sm:grid-cols-2">
        {selectField(
          'salutation',
          t('PROFILE_CONVERSATION_SALUTATION', { defaultValue: 'Salutation' }),
          salutationOptions
        )}
        {values.salutation === 'custom'
          ? textField(
              'salutationText',
              t('PROFILE_CONVERSATION_SALUTATION_TEXT', { defaultValue: 'Custom salutation' })
            )
          : null}
      </div>

      <div className="flex flex-col gap-2">
        <div className="grid gap-5 sm:grid-cols-2">
          {textField(
            'givenName',
            t('PROFILE_CONVERSATION_GIVEN_NAME', { defaultValue: 'First name' }),
            {
              hintId: namesHintId,
            }
          )}
          {textField(
            'familyName',
            t('PROFILE_CONVERSATION_FAMILY_NAME', { defaultValue: 'Last name' }),
            {
              hintId: namesHintId,
            }
          )}
        </div>
        <p id={namesHintId} className="text-sm text-muted-foreground">
          {t('PROFILE_CONVERSATION_NAMES_HINT', {
            defaultValue: 'Only for how Buddy and your agents address you.',
            context,
          })}
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {selectField(
          'industry',
          t('PROFILE_CONVERSATION_INDUSTRY', { defaultValue: 'Industry' }),
          industryOptions
        )}
        {values.industry === 'other'
          ? textField(
              'industryText',
              t('PROFILE_CONVERSATION_INDUSTRY_TEXT', { defaultValue: 'Custom industry' })
            )
          : null}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {selectField(
          'workArea',
          t('PROFILE_CONVERSATION_WORK_AREA', { defaultValue: 'Area' }),
          areaOptions,
          areaChoice,
          handleAreaSelect
        )}
        {showAreaText
          ? textField(
              'workArea',
              t('PROFILE_CONVERSATION_WORK_AREA_TEXT', { defaultValue: 'Custom area' })
            )
          : null}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={!isDirty || hasErrors || save.isPending}
          onClick={() => void handleSave()}
        >
          {t('SAVE', { defaultValue: 'Save' })}
        </Button>
      </div>
    </div>
  );
}
